import { createContext, useCallback, useContext, useEffect, useState } from "react";

/**
 * App-wide unsaved-changes registry.
 *
 * Forms register/unregister + flip dirty state via the global window bus
 * (`window.__unsavedRegistry`). The bus is wired up here on mount.
 *
 * Why a window bus instead of the context hook?
 * The `visual-edits` Babel plugin in this codebase cannot trace prop
 * sources through a context-hook import inside form/wizard files (it
 * crashes with "Cannot read properties of null (reading 'traverse')" at
 * the ImportDeclaration step). Using a window-level event bus sidesteps
 * the cross-file prop tracing entirely while keeping the same React
 * rerender behaviour for the indicator.
 */
const UnsavedChangesContext = createContext({ entries: [] });

export function UnsavedChangesProvider({ children }) {
  // Map<key, { key, label, location, lastEditedAt: number, dirty: bool }>
  const [entriesMap, setEntriesMap] = useState({});
  // tick state forces a re-render every 30s so "X minutes ago" stays fresh
  const [, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick(v => v + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const register = useCallback((key, label, location) => {
    setEntriesMap(prev => {
      if (prev[key]) return prev;
      return { ...prev, [key]: { key, label, location, lastEditedAt: 0, dirty: false } };
    });
  }, []);

  const markDirty = useCallback((key) => {
    setEntriesMap(prev => {
      if (!prev[key]) return prev;
      return {
        ...prev,
        [key]: { ...prev[key], dirty: true, lastEditedAt: Date.now() },
      };
    });
  }, []);

  const markClean = useCallback((key) => {
    setEntriesMap(prev => {
      if (!prev[key]) return prev;
      return { ...prev, [key]: { ...prev[key], dirty: false } };
    });
  }, []);

  const unregister = useCallback((key) => {
    setEntriesMap(prev => {
      if (!prev[key]) return prev;
      const n = { ...prev };
      delete n[key];
      return n;
    });
  }, []);

  // Expose the API on window so any form can register without importing
  // anything from this file (avoids the visual-edits plugin issue).
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__unsavedRegistry = { register, markDirty, markClean, unregister };
    }
    return () => {
      if (typeof window !== "undefined") {
        delete window.__unsavedRegistry;
      }
    };
  }, [register, markDirty, markClean, unregister]);

  const entries = Object.values(entriesMap)
    .filter(e => e.dirty)
    .sort((a, b) => b.lastEditedAt - a.lastEditedAt);

  return (
    <UnsavedChangesContext.Provider value={{ entries }}>
      {children}
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChanges() {
  return useContext(UnsavedChangesContext);
}

export default UnsavedChangesContext;
